import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CloudDownload } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import ToolkitComparePage from './toolkit/ToolkitComparePage';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch from './toolkit/useToolkitBatch';
import { useToolkitProject } from './toolkit/ToolkitProject';

// Sync › project › Toolkit › Balance Reservations — ported from the desktop's Balancing › Reservations. A v5 Res Detail
// report against the Opera Cloud one (XML or delimited, each), matched v5 confirmation number ↔ Cloud external
// reference (toolkit compare.js reservations): Match, Partial (dates, status, room type or rate code differ), Missing
// in Cloud, Cloud only, Possible duplicate.
//
// ACTIONS: a reservation cancelled in v5 (CXL) but not in Opera Cloud can be cancelled there, with a cancellation reason
// from Opera Cloud — one at a time or all of them.

const COLUMNS = [
    { key: 'v5ConfNumber', label: 'v5 conf' },
    { key: 'cloudConfNumber', label: 'Cloud conf' },
    { key: 'v5GuestName', label: 'Guest (v5)', render: (r) => r.v5GuestName || r.cloudGuestName },
    { key: 'v5ArrivalDate', label: 'Arrival v5 / Cloud', render: (r) => `${r.v5ArrivalDate || '—'} / ${r.cloudArrivalDate || '—'}` },
    { key: 'v5DepartureDate', label: 'Departure v5 / Cloud', render: (r) => `${r.v5DepartureDate || '—'} / ${r.cloudDepartureDate || '—'}` },
    { key: 'v5ReservationStatus', label: 'Status v5 / Cloud', render: (r) => `${r.v5ReservationStatus || '—'} / ${r.cloudReservationStatus || '—'}` },
    { key: 'v5RoomType', label: 'Room v5 / Cloud', render: (r) => `${r.v5RoomType || '—'} / ${r.cloudRoomType || '—'}` },
    { key: 'v5RateCode', label: 'Rate v5 / Cloud', render: (r) => `${r.v5RateCode || '—'} / ${r.cloudRateCode || '—'}` },
    { key: 'differences', label: 'Differences' },
];
const STATUSES = [
    { value: 'partial', label: 'Partial' }, { value: 'missing', label: 'Missing in Cloud' },
    { value: 'cloud-only', label: 'Cloud only' }, { value: 'cloud-duplicate', label: 'Possible duplicate' },
];

const summaryOf = (s) => [
    { label: 'v5 reservations', value: s.v5Total }, { label: 'Cloud reservations', value: s.cloudTotal },
    { label: 'Match', value: s.match, tone: 'ok' }, { label: 'Partial', value: s.partial, tone: 'warn' },
    { label: 'Missing in Cloud', value: s.missing, tone: 'fail' }, { label: 'Cloud only', value: s.cloudOnly },
    { label: 'Possible duplicates', value: s.potentialDuplicates, tone: 'warn' },
];

function ToolkitBalanceReservationsPage() {
    const [dateFormat, setDateFormat] = useState('');
    return (
        <ToolkitComparePage title="Balance Reservations"
            subtitle="Compare an Opera v5 Res Detail report with the Opera Cloud one, reservation by reservation."
            kind="reservations" accept=".xml,.rep,.csv,.txt" exportName="Reservation_Balance"
            v5Title="Opera v5 Res Detail report" v5Label="Choose the v5 report (XML or CSV)"
            cloudTitle="Opera Cloud Res Detail report" cloudLabel="Choose the Cloud report (XML or CSV)"
            columns={COLUMNS} statuses={STATUSES} summaryOf={summaryOf}
            optionValues={{ hotelDateFormat: dateFormat || null }}
            options={(
                <label>
                    <span>Ambiguous dates (04.05.26)</span>
                    <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                        <option value="">Decide from the file (else month first)</option>
                        <option value="dd-MM-yy">Day first</option>
                        <option value="MM-dd-yy">Month first</option>
                    </select>
                </label>
            )}>
            {(result) => <CancelActions rows={result.rows} />}
        </ToolkitComparePage>
    );
}

// Cancel in Opera Cloud what v5 has cancelled.
function CancelActions({ rows }) {
    const { ctx, ready } = useToolkitProject();
    const [reasons, setReasons] = useState([]);
    const [reason, setReason] = useState('');
    const [msg, setMsg] = useState('');
    const items = useMemo(() => rows.filter((r) => (r.status === 'match' || r.status === 'partial')
        && r.v5ReservationStatus === 'CXL' && r.cloudReservationStatus !== 'CXL'), [rows]);
    const chosen = reasons.find((r) => r.code === reason);
    const request = useCallback((row) => ({ area: 'property', op: 'cancel-reservation', args: {
        v5ConfNumber: row.v5ConfNumber, reasonCode: chosen.code, reasonDescription: chosen.description,
    } }), [chosen]);
    const batch = useToolkitBatch({ request, ctx,
        server: { screen: 'balance-reservations-cancel', title: 'Cancel reservations (balancing)', keyOf: (row) => row.v5ConfNumber } });
    const { load } = batch;
    useEffect(() => { load(items.map((r) => ({ v5ConfNumber: r.v5ConfNumber, cloudConfNumber: r.cloudConfNumber,
        guest: r.v5GuestName || r.cloudGuestName, cloudStatus: r.cloudReservationStatus }))); }, [items, load]);

    const loadReasons = async () => {
        setMsg('Loading cancellation reasons…');
        try {
            const list = (await toolkitService.lookup(ctx, 'cancellation-reasons')).data.data || [];
            const mapped = list.map((i) => ({ code: i.code || i.cancellationCode || '', description: i.description || i.name || i.displayValue || i.code || '' })).filter((r) => r.code);
            setReasons(mapped);
            setMsg(mapped.length ? '' : 'No cancellation reasons returned from Opera Cloud.');
        } catch (err) {
            setMsg(err?.response?.data?.message || err.message);
        }
    };

    if (!items.length) return null;
    return (
        <div className="tkb-step" style={{ marginTop: '1rem' }}>
            <div className="tkb-step-head"><span className="tkb-num">4</span> Cancelled in v5, not in Opera Cloud — {items.length}</div>
            <div className="tkb-row">
                <Button variant="secondary" onClick={loadReasons} disabled={!ready}><CloudDownload className="icon" /> Load cancellation reasons</Button>
                <label>
                    <span>Reason</span>
                    <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={!reasons.length}>
                        <option value="">{reasons.length ? 'Choose…' : 'Load the reasons first'}</option>
                        {reasons.map((r) => <option key={r.code} value={r.code}>{r.code}{r.description && r.description !== r.code ? ` — ${r.description}` : ''}</option>)}
                    </select>
                </label>
            </div>
            {msg && <p className="tkb-msg">{msg}</p>}
            <ToolkitBatchTable batch={batch} exportName="Reservation_Cancellations" canRun={ready && !!chosen}
                columns={[{ key: 'v5ConfNumber', label: 'v5 conf' }, { key: 'cloudConfNumber', label: 'Cloud conf' },
                    { key: 'guest', label: 'Guest' }, { key: 'cloudStatus', label: 'Cloud status' }, { key: 'result', label: 'Result' }]}
                searchKeys={['v5ConfNumber', 'cloudConfNumber', 'guest']} />
        </div>
    );
}

export default ToolkitBalanceReservationsPage;
